import { useEffect, useRef } from 'react';
import { AudioClip, Composition } from '../timeline/types';
import { useMedia } from '../media';
import { playheadStore } from './playhead-store';

const DRIFT_THRESHOLD_S = 0.5;
const PRECISE_THRESHOLD_S = 0.05;
const SCRUB_DELTA_MS = 200;
/** Umbral bajo el cual no reescribimos `el.volume` en un fade - evita writes por frame. */
const VOLUME_EPSILON = 0.01;

type UseAudioSyncOptions = {
  composition: Composition;
  isPlaying: boolean;
  volume: number;
  muted: boolean;
};

/**
 * Drives HTMLAudio elements from the timeline state.
 * Mounts one `HTMLAudioElement` per `AudioClip` (keyed by clip id) and keeps it
 * in sync with the playhead. Mirrors useVideoSync: resyncs `currentTime` only on
 * boundary cross, scrub, or large drift — otherwise the audio plays on its own
 * clock. Aggressive per-tick seeks were causing robotic / glitchy playback.
 */
export const useAudioSync = ({ composition, isPlaying, volume, muted }: UseAudioSyncOptions): void => {
  const elementsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const previousAliveRef = useRef<Map<string, boolean>>(new Map());
  const previousPlayheadRef = useRef<number>(playheadStore.get());
  const { registerAudio } = useMedia();

  useEffect(() => {
    const elements = elementsRef.current;
    const seen = new Set<string>();

    for (const clip of Object.values(composition.clipsById)) {
      if (clip.kind !== 'audio') continue;
      const ac = clip as AudioClip;
      seen.add(ac.id);

      const existing = elements.get(ac.id);
      if (!existing) {
        const el = new Audio();
        el.crossOrigin = 'anonymous';
        el.preload = 'auto';
        el.volume = (ac.gain ?? 1) * volume;
        el.muted = muted;
        el.src = ac.src;
        elements.set(ac.id, el);
        // Route through AudioContext so audio clips reach both speakers and the export stream.
        registerAudio(el);
      } else {
        if (existing.src !== ac.src) existing.src = ac.src;
        existing.volume = (ac.gain ?? 1) * volume;
        existing.muted = muted;
      }
    }

    const toDelete: string[] = [];
    elements.forEach((el, id) => {
      if (!seen.has(id)) {
        el.pause();
        el.src = '';
        toDelete.push(id);
      }
    });
    toDelete.forEach(id => {
      elements.delete(id);
      previousAliveRef.current.delete(id);
    });
  }, [composition, volume, muted, registerAudio]);

  useEffect(() => {
    const elements = elementsRef.current;

    const sync = () => {
      const t = playheadStore.get();
      const playheadDelta = Math.abs(t - previousPlayheadRef.current);
      const isScrub = playheadDelta > SCRUB_DELTA_MS;
      previousPlayheadRef.current = t;

      elements.forEach((el, clipId) => {
        const clip = composition.clipsById[clipId];
        if (!clip || clip.kind !== 'audio') {
          if (!el.paused) el.pause();
          return;
        }
        const ac = clip as AudioClip;
        const trimIn = ac.trim?.in ?? 0;
        const alive = t >= ac.start && t < ac.start + ac.duration;

        const wasAlive = previousAliveRef.current.get(clip.id) ?? false;
        const boundaryCross = alive && !wasAlive;
        previousAliveRef.current.set(clip.id, alive);

        if (!alive) {
          if (!el.paused) el.pause();
          return;
        }

        // Rampa lineal de gain → 0 en los últimos `fadeOutMs` ms del clip.
        // Se computa por tick durante la ventana de fade y se escribe a `el.volume`,
        if (ac.fadeOutMs && ac.fadeOutMs > 0) {
          const clipEndMs = ac.start + ac.duration;
          const fadeDurationMs = Math.min(ac.fadeOutMs, ac.duration);
          const fadeStartMs = clipEndMs - fadeDurationMs;
          const base = (ac.gain ?? 1) * volume;
          const targetVol = t >= fadeStartMs ? base * Math.max(0, 1 - (t - fadeStartMs) / fadeDurationMs) : base;
          if (Math.abs(el.volume - targetVol) > VOLUME_EPSILON) {
            el.volume = Math.max(0, Math.min(1, targetVol));
          }
        }

        const localTimeS = (t - ac.start + trimIn) / 1000;

        if (isPlaying) {
          if (el.paused) el.play().catch(() => undefined);
          const drift = Math.abs(el.currentTime - localTimeS);
          const tolerance = boundaryCross || isScrub ? PRECISE_THRESHOLD_S : DRIFT_THRESHOLD_S;
          if (drift > tolerance) {
            el.currentTime = localTimeS;
          }
        } else {
          if (!el.paused) el.pause();
          if (Math.abs(el.currentTime - localTimeS) > PRECISE_THRESHOLD_S) {
            el.currentTime = localTimeS;
          }
        }
      });
    };

    sync();
    return playheadStore.subscribe(sync);
  }, [composition, isPlaying]);

  useEffect(() => {
    const elements = elementsRef.current;
    return () => {
      elements.forEach(el => {
        el.pause();
        el.src = '';
      });
      elements.clear();
      previousAliveRef.current.clear();
    };
  }, []);
};
